import { Injectable } from '@angular/core';
import i18next, { i18n } from 'i18next';
import { GeneralCache } from 'src/app/classes/app/general-cache';
import { EAppIcons } from 'src/app/classes/def/app/icons';
import { AppConstants } from 'src/app/classes/app/constants';
import { IPopoverActions } from 'src/app/classes/def/app/modal-interaction';
import { EModalTypes } from 'src/app/classes/utils/uiext';
import { ESettingsCode } from 'src/app/classes/def/app/settings';
import { IObservableMultiplex } from 'src/app/classes/def/mp/subs';
import { BehaviorSubject } from 'rxjs';
import { IDBModelLanguage } from 'src/app/classes/def/nav-params/story';
import { ResourcesCoreDataService } from '../../data/resources-core';
import { UserDataService } from '../../data/user';
import { SleepUtils } from '../../utils/sleep-utils';
import { GenericDataService } from '../data/generic';
import { UiExtensionService } from './ui-extension';
import { UiExtensionStandardService } from './ui-extension-standard';
import { ITTSVoice, TextToSpeechService } from '../apis/tts';
import { Messages } from 'src/app/classes/def/app/messages';
import { ELocalAppDataKeys, IAppFlagsElement } from 'src/app/classes/def/app/storage-flags';
import { StorageOpsService } from '../data/storage-ops';


export interface IAppTranslationResponseContent {
    lng: string;
    version?: number;
    resources: Record<string, any>; // { ui: {...}, auth: {...}, home: {...} }
};

export interface IAppTranslationResponse {
    lng: string,
    ns: string,
    content: IAppTranslationResponseContent
};

export interface ILangOpt {
    langId: number,
    langCode: string,
    langIcon: string,
    ttsVoice: string
};

interface IObservableMultiplexSpec extends IObservableMultiplex {
    initialized: BehaviorSubject<boolean>,
    languageChanged: BehaviorSubject<boolean>
}

@Injectable({ providedIn: 'root' })
export class I18nService {
    private initialized = false;

    observable: IObservableMultiplexSpec = {
        initialized: null,
        languageChanged: null
    };

    langId: number = GeneralCache.langId; // default 'en'
    langs: IDBModelLanguage[] = null;
    langIcon: string = EAppIcons.translate;
    langCode: string = "en-US";
    ttsVoice: string = null;

    constructor(
        public generic: GenericDataService,
        public uiextStandard: UiExtensionStandardService,
        public uiext: UiExtensionService,
        public userData: UserDataService,
        public resourcesCore: ResourcesCoreDataService,
        public tts: TextToSpeechService,
        public storageOps: StorageOpsService
    ) {
        console.log("i18n service created");
        this.observable.initialized = new BehaviorSubject(null);
        this.observable.languageChanged = new BehaviorSubject(null);
    }

    /**
     * mongo: { lng: "ro", ns: "notification", resources: {...} }
     * i18next: i18next.resources.ro.notification = {...}
     * @param defaultLng 
     * @returns 
     */
    init(defaultLng = 'en') {
        return new Promise(async (resolve, reject) => {
            try {
                if (this.initialized) {
                    console.warn("i18n already initialized");
                    resolve(false);
                    return;
                };

                await i18next.init({
                    lng: defaultLng,
                    fallbackLng: 'en',
                    // declare the namespaces you intend to use in templates
                    // used as ns:resource.resource...
                    // ui_bundle it's loaded as multiple namespaces (expanded) such as defined in resources
                    ns: [
                        'ui', 'auth', 'home', 'activity', 'story', // from ui_bundle
                        'msg', 'toast', 'tts', 'mp', 'notification' // others
                    ],
                    defaultNS: 'ui',
                    interpolation: { escapeValue: false }
                });

                // Load UI bundle right away (fast first paint)
                await this.loadUiBundle(defaultLng);
                // Ensure Messages.* is hydrated even before any language switch
                this.hydrate();

                this.initialized = true;
                this.observable.initialized.next(true);
                resolve(true);
            } catch (err) {
                console.error(err);
                reject(err);
            }
        });
    }

    checkWaitTranslationsLoaded() {
        return new Promise((resolve) => {
            if (this.initialized) {
                resolve(true);
                return;
            }
            this.observable.initialized.subscribe((loaded: boolean) => {
                if (loaded) {
                    resolve(true);
                }
            }, (err: Error) => {
                console.error(err);
                resolve(false);
            });
        });
    }

    changeLanguage(lng: string) {
        return new Promise(async (resolve, reject) => {
            try {
                // ensure init happened
                if (!this.initialized) await this.init(lng);

                // load UI bundle for target language first (so UI updates instantly)
                await this.loadUiBundle(lng);
                await i18next.changeLanguage(lng);

                this.hydrate();
                console.log("translation ui bundle loaded");
                resolve(true);
            } catch (err) {
                console.error(err);
                reject(err);
            }
        });
    }

    private loadUiBundle(lng: string) {
        return new Promise(async (resolve, reject) => {
            try {
                let res: IAppTranslationResponse = await this.generic.genericGetNoAuthWData("/resources/get-app-translations", {
                    lng: lng,
                    ns: "ui_bundle"
                });
                // register each namespace inside the bundle
                for (const [ns, data] of Object.entries(res.content.resources || {})) {
                    // deep=true merge; overwrite=true allows server to override existing keys
                    i18next.addResourceBundle(lng, ns.replace("connect_", ""), data, true, true);
                }

                // let nss: string[] = ["connect_msg", "connect_notification", "connect_tts", "connect_toast", "connect_mp"];
                let nss: string[] = ["connect_msg", "connect_notification", "connect_tts", "connect_toast"];
                for (let ns of nss) {
                    res = await this.generic.genericGetNoAuthWData("/resources/get-app-translations", {
                        lng: lng,
                        ns: ns
                    });
                    i18next.addResourceBundle(lng, ns.replace("connect_", ""), res.content.resources, true, true);
                }

                // console.log("loaded translations: ", i18next.getResourceBundle(lng, "msg"));
                console.log("loaded translation resource bundles");

                resolve(true);
            } catch (err) {
                console.error(err);
                reject(err);
            }
        });
    }

    t(key: string, options?: any) {
        return i18next.t(key, options);
    }

    get instance(): i18n {
        return i18next;
    }

    private joinPath(basePath: string, key: string): string {
        if (!basePath) return key;

        // If basePath already contains namespace separator
        if (basePath.endsWith(':')) {
            return `${basePath}${key}`; // notification:destinationReached
        }

        return `${basePath}.${key}`;  // normal object nesting
    }


    private hydrateByPath<T>(obj: T, basePath = ""): T {
        if (typeof obj === "string") {
            return i18next.exists(basePath)
                ? (i18next.t(basePath) as any)
                : (obj as any);
        }

        if (Array.isArray(obj)) {
            return obj.map((v, i) =>
                this.hydrateByPath(v as any, this.joinPath(basePath, String(i)))
            ) as any;
        }

        if (obj && typeof obj === "object") {
            const out: any = {};
            for (const key of Object.keys(obj as any)) {
                const nextPath = this.joinPath(basePath, key);
                out[key] = this.hydrateByPath((obj as any)[key], nextPath);
            }
            return out;
        }

        return obj;
    }


    private hydrate() {
        Messages.notification = this.hydrateByPath(Messages.notification, "notification:");
        // Messages.mp = this.hydrateByPath(Messages.mp, "mp:");
        Messages.toast = this.hydrateByPath(Messages.toast, "toast:");
        Messages.tts = this.hydrateByPath(Messages.tts, "tts:");
        Messages.msg = this.hydrateByPath(Messages.msg, "msg:");
        console.log("hydrated ui messages");
    }



    setLangIcon() {
        // GeneralCache.resourceCache.general.appServices.content.object.mp.url
    }

    syncSelectedLanguageFlags(lang: IDBModelLanguage) {
        this.langId = lang.id;
        GeneralCache.langId = this.langId;
        this.langIcon = lang.icon;
        this.langCode = lang.langCode;
        this.ttsVoice = lang.ttsVoice;
    }

    getSelectedLanguage() {
        let opt: ILangOpt = {
            langId: this.langId,
            langCode: this.langCode,
            langIcon: this.langIcon,
            ttsVoice: this.ttsVoice
        };
        return opt;
    }

    async configureSelectedLanguage(langId: number) {
        console.log("configure selected language langId: ", langId);
        this.langId = langId;
        await this.preloadLangOptions();
        if (!this.langs) {
            return;
        }
        let langCode: string = "en";
        let langNameLocale: string = "English";
        for (let lang of this.langs) {
            if (lang.id === this.langId) {
                this.syncSelectedLanguageFlags(lang);
                langCode = lang.langCode;
                langNameLocale = lang.nameLocale;
                let iodash: number = langCode.indexOf("-");
                if (iodash !== -1) {
                    langCode = langCode.slice(0, iodash);
                }
                break;
            }
        }
        console.log("selected language: ", this.langId, langCode);
        if (langCode != null) {
            await this.changeLanguage(langCode);
            this.configureLanguage(langNameLocale, false);
        }
    }

    async configureLanguage(langNameLocale: string, speak: boolean) {
        console.log("configure language: ", langNameLocale);
        this.configureTTSApp();
        if (speak) {
            await SleepUtils.sleep(100);
            this.tts.textToSpeechQueue(langNameLocale, false, 1, () => {
                console.log("tts queue finished");
            }, 120000, false);
        }
    }

    configureTTSApp() {
        let langOpt: ILangOpt = this.getSelectedLanguage();
        console.log("lang opt: ", langOpt);
        let defaultVoice: ITTSVoice = {
            name: langOpt.langCode,
            lang: langOpt.langCode,
            voice: langOpt.ttsVoice != null ? langOpt.ttsVoice : null
        };
        this.tts.setTTSVoiceOptionsApp(defaultVoice);
    }

    async preloadLangOptions() {
        if (!this.langs) {
            if (!GeneralCache.resourceCache.general.languages?.loaded) {
                this.langs = await this.resourcesCore.getAppLanguages();
                console.log("loaded language options: ", this.langs);
            } else {
                this.langs = GeneralCache.resourceCache.general.languages.content;
            }
        }
    }

    async showLangOptions() {
        try {
            console.log("switch language");
            await this.preloadLangOptions();
            let actions: IPopoverActions = {};
            for (let lang of this.langs) {
                actions["" + lang.id] = {
                    name: lang.nameLocale,
                    code: lang.id,
                    icon: lang.icon,
                    customIcon: true,
                    enabled: true
                }
            }
            let result = await this.uiextStandard.showStandardModal(null, EModalTypes.options, null, {
                view: {
                    fullScreen: false,
                    transparent: AppConstants.transparentMenus,
                    large: false,
                    addToStack: true,
                    frame: false
                },
                params: { actions: actions }
            });
            console.log("selected language: ", result);
            let langCode: string = null;
            let langNameLocale: string = "English";

            for (let lang of this.langs) {
                if (lang.id === result) {
                    this.syncSelectedLanguageFlags(lang);
                    langCode = lang.langCode;
                    langNameLocale = lang.nameLocale;
                    let iodash: number = langCode.indexOf("-");
                    if (iodash !== -1) {
                        langCode = langCode.slice(0, iodash);
                    }
                    break;
                }
            }
            if (langCode != null) {
                await this.uiext.showLoadingV2Queue("Loading..");
                await this.switchLanguageOption(langCode, true);
                await this.uiext.dismissLoadingV2();
            }
        } catch (err) {
            console.error(err);
        }
    }

    async switchLanguageOption(langCode: string, tts: boolean) {
        await this.preloadLangOptions();
        if (!this.langs) {
            return;
        }
        await this.changeLanguage(langCode);
        this.setLanguageOption();
        let langNameLocale: string = "";
        let lang: IDBModelLanguage = this.getLang(langCode);
        if (lang != null) {
            this.syncSelectedLanguageFlags(lang);
            langNameLocale = lang.nameLocale;
        }
        await this.configureLanguage(langNameLocale, tts);
        this.observable.languageChanged.next(true);
    }

    getLang(langCode: string) {
        let langData: IDBModelLanguage = null;
        for (let lang of this.langs) {
            let lc: string = lang.langCode;
            let iodash: number = lc.indexOf("-");
            if (iodash !== -1) {
                lc = lc.slice(0, iodash);
            }
            if (lc === langCode) {
                langData = lang;
                break;
            }
        }
        return langData;
    }

    getLanguageOption() {
        return new Promise<number>((resolve) => {
            (async () => {
                try {
                    // Ensure Ionic storage is initialized before reading flags.
                    await this.storageOps.getStorage();
                    const flag = this.getLanguageStorageFlag();
                    const localValue: number = await this.storageOps.checkStorageFlagResolve(flag, true);
                    if (localValue != null) {
                        this.langId = localValue;
                        // Boot from local value immediately; keep server sync async.
                        resolve(this.langId);
                        this.userData.getFlagServer(ESettingsCode.userLanguage).then((value: number) => {
                            if (value != null) {
                                this.langId = value;
                                this.setLanguageOptionLocal(this.langId);
                            }
                        }).catch((err: Error) => {
                            console.error(err);
                        });
                        return;
                    }
                } catch (err) {
                    console.error(err);
                }

                this.userData.getFlagServer(ESettingsCode.userLanguage).then((value: number) => {
                    console.log("get language option: ", value);
                    if (value != null) {
                        this.langId = value;
                        this.setLanguageOptionLocal(this.langId);
                    }
                    resolve(this.langId);
                }).catch((err: Error) => {
                    console.error(err);
                    resolve(this.langId);
                });
            })();
        });
    }

    setLanguageOption() {
        this.setLanguageOptionLocal(this.langId);
        this.userData.setFlagServer(ESettingsCode.userLanguage, this.langId).then(() => {
            console.log("language option flag set");
        }).catch((err: Error) => {
            console.error(err);
        });
    }

    private getLanguageStorageFlag(langId = this.langId): IAppFlagsElement {
        return {
            flag: ELocalAppDataKeys.languageId,
            value: langId
        };
    }

    private setLanguageOptionLocal(langId: number) {
        if (langId == null) {
            return;
        }
        this.storageOps.setStorageFlagNoAction(this.getLanguageStorageFlag(langId));
    }

    watchLanguageChanged() {
        return this.observable.languageChanged;
    }

}
